import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../../../app/context/useApp";
import { useGenerationNotifications } from "../../../app/context/GenerationNotificationContext";
import { getAllActiveSilos } from "../../../core/services/silos.service";
import {
  getConversation,
  getLatestConversation,
  deleteConversationById,
} from "../../../core/services/conversation.service";
import { messageService } from "../../../core/services/message.service";
import {
  generateStream,
  getGenerateAiNameFromPath,
  hydrateGenerateMessagesMedia,
} from "../../../core/services/generate.service";
import {
  extractApiErrorMessage,
  getUserErrorMessage,
  logApiError,
} from "../../../core/utils/apiErrorUtils";
import { getAuthToken } from "../../../core/utils/authUtils";
import {
  extractConversationId,
  getSessionId,
  isTemporaryConversationId,
  mapApiMessagesToGenerateMessages,
} from "../utils/conversationUtils";

const getClientId = (client) =>
  client?.clientId ?? client?.Id ?? client?.id ?? null;

const getProjectId = (project) =>
  project?.projectId ?? project?.ProjectId ?? project?.id ?? null;

const buildDownloadFilename = (promptText, mediaType) => {
  const sanitizedPrompt = promptText
    ? promptText
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()
        .substring(0, 30)
    : "generated-media";

  return `${sanitizedPrompt}.${mediaType === "video" ? "mp4" : "png"}`;
};

const parseGenerateStreamResult = (raw, mediaType) => {
  const text = String(raw ?? "").trim();
  if (!text) {
    throw new Error("Empty response from generate API.");
  }

  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = null;
  }

  if (mediaType === "content") {
    const contentData =
      parsed?.content && typeof parsed.content === "object"
        ? parsed.content
        : parsed && typeof parsed === "object"
          ? parsed
          : null;

    if (!contentData || typeof contentData !== "object") {
      throw new Error("Invalid content response.");
    }
    return { kind: "content", data: contentData };
  }

  const mediaUrl =
    (parsed && typeof parsed === "object"
      ? parsed.url ??
        parsed.mediaUrl ??
        parsed.imageUrl ??
        parsed.videoUrl ??
        parsed.content
      : null) ??
    (text.startsWith("http") || text.startsWith("data:") ? text : null);

  if (!mediaUrl) {
    if (text.length > 100 && /^[A-Za-z0-9+/=\s]+$/.test(text)) {
      const mime = mediaType === "video" ? "video/mp4" : "image/png";
      return {
        kind: mediaType,
        url: `data:${mime};base64,${text.replace(/\s/g, "")}`,
      };
    }
    throw new Error(
      extractApiErrorMessage(
        parsed ?? { message: text },
        parsed?.message || parsed?.error || text || "Failed to generate media.",
      ),
    );
  }

  return { kind: mediaType, url: String(mediaUrl) };
};

const revokeBlobUrls = (blobUrlsRef) => {
  blobUrlsRef.current.forEach((url) => {
    if (url) URL.revokeObjectURL(url);
  });
  blobUrlsRef.current.clear();
};

export default function useChatGptPage() {
  const {
    selectedClient,
    selectedProject,
    selectedSilo,
    chatType,
    currentChatSession,
    setCurrentChatSession,
    createNewChat,
    loadChatSession,
    renameChatSession,
    renamingConversationIds,
    showAlert,
  } = useApp();
  const {
    startGenerationTask,
    completeGenerationTask,
    failGenerationTask,
  } = useGenerationNotifications();
  const location = useLocation();
  const navigate = useNavigate();

  const isScriptOnlyPage = location.pathname.includes("/generate/claude");

  const [generateSiloId, setGenerateSiloId] = useState(null);
  const [activeGenerateConversationId, setActiveGenerateConversationId] =
    useState(null);
  const [generateConversations, setGenerateConversations] = useState([]);
  const [loadingGenerateConversations, setLoadingGenerateConversations] =
    useState(false);
  const [deletingGenerateConversationIds, setDeletingGenerateConversationIds] =
    useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [showVideoNotificationPrompt, setShowVideoNotificationPrompt] =
    useState(false);

  const [messages, setMessages] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [mediaType, setMediaType] = useState("image");
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  const messagesEndRef = useRef(null);
  const blobUrlsRef = useRef(new Map());
  const prevMessageCountRef = useRef(0);
  const handledNotificationConversationRef = useRef(null);
  const pendingVideoNotificationRef = useRef(null);
  const activeVideoNotificationTaskIdRef = useRef(null);
  const completedVideoNotificationRef = useRef(null);

  const resolvedConversationId = useMemo(() => {
    const sessionId = getSessionId(currentChatSession);
    if (sessionId && !isTemporaryConversationId(sessionId)) {
      return sessionId;
    }
    if (
      activeGenerateConversationId &&
      !isTemporaryConversationId(activeGenerateConversationId)
    ) {
      return activeGenerateConversationId;
    }
    return null;
  }, [currentChatSession, activeGenerateConversationId]);

  useEffect(() => {
    let active = true;

    const loadGenerateSiloId = async () => {
      try {
        const response = await getAllActiveSilos();
        const silos = Array.isArray(response?.data) ? response.data : [];
        const generateSilo = silos.find(
          (silo) => String(silo?.silosName || "").toLowerCase() === "generate",
        );
        if (active) {
          setGenerateSiloId(generateSilo?.silosId ?? null);
        }
      } catch (error) {
        logApiError("Load generate silo", error);
        if (active) {
          setGenerateSiloId(null);
        }
      }
    };

    loadGenerateSiloId();
    return () => {
      active = false;
    };
  }, []);

  const refreshGenerateConversations = useCallback(async () => {
    const clientId = getClientId(selectedClient);
    if (!generateSiloId || !clientId) {
      setGenerateConversations([]);
      return;
    }

    setLoadingGenerateConversations(true);
    try {
      const response = await getConversation(generateSiloId, {
        isGlobal: true,
        clientId,
        projectId: getProjectId(selectedProject),
      });
      const conversationData = Array.isArray(response?.data) ? response.data : [];
      setGenerateConversations(
        conversationData.filter(
          (session) => !isTemporaryConversationId(getSessionId(session)),
        ),
      );
    } catch (error) {
      logApiError("Load generate conversations", error);
      setGenerateConversations([]);
    } finally {
      setLoadingGenerateConversations(false);
    }
  }, [generateSiloId, selectedClient, selectedProject]);

  useEffect(() => {
    refreshGenerateConversations();
  }, [refreshGenerateConversations]);

  useEffect(() => {
    const prevCount = prevMessageCountRef.current;
    const nextCount = messages.length;

    if (nextCount > prevCount) {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
    }

    prevMessageCountRef.current = nextCount;
  }, [messages]);

  useEffect(() => {
    return () => revokeBlobUrls(blobUrlsRef);
  }, []);

  useEffect(() => {
    if (isScriptOnlyPage) {
      setMediaType("content");
    }
  }, [isScriptOnlyPage]);

  const resetLocalChat = useCallback(() => {
    revokeBlobUrls(blobUrlsRef);
    setMessages([]);
    setPrompt("");
    setShowVideoNotificationPrompt(false);
    pendingVideoNotificationRef.current = null;
    activeVideoNotificationTaskIdRef.current = null;
    completedVideoNotificationRef.current = null;
  }, []);

  const persistConversationAfterSend = useCallback(
    async (silosId, promptText) => {
      let conversationId = resolvedConversationId;

      if (!conversationId) {
        const latestResponse = await getLatestConversation(Number(silosId));
        conversationId = extractConversationId(latestResponse?.data);
      }

      if (!conversationId) return null;

      setActiveGenerateConversationId(conversationId);
      setCurrentChatSession((prev) => {
        if (!prev) return prev;
        const title =
          promptText.length > 40
            ? `${promptText.substring(0, 40).trim()}...`
            : promptText;
        return {
          ...prev,
          conversationsId: conversationId,
          conversationId,
          title: prev.title === "New Chat" ? title : prev.title,
        };
      });

      await refreshGenerateConversations();
      return conversationId;
    },
    [
      resolvedConversationId,
      refreshGenerateConversations,
      setCurrentChatSession,
    ],
  );

  const handleDownload = useCallback(
    (mediaUrl, promptText) => {
      const link = document.createElement("a");
      link.href = mediaUrl;
      link.download = buildDownloadFilename(promptText, mediaType);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [mediaType],
  );

  const revealGeneratedVideo = useCallback((messageId) => {
    setMessages((prev) =>
      prev.map((msg) => {
        if (msg.id !== messageId) return msg;

        return {
          ...msg,
          loading: false,
          videoReady: false,
          content: msg.generatedContent ?? msg.content,
          generatedContent: undefined,
          isVideo: true,
        };
      }),
    );
  }, []);

  const dismissVideoNotificationPrompt = useCallback(() => {
    pendingVideoNotificationRef.current = null;
    setShowVideoNotificationPrompt(false);
  }, []);

  const confirmVideoNotification = useCallback(() => {
    const pending = pendingVideoNotificationRef.current;
    if (!pending) {
      setShowVideoNotificationPrompt(false);
      return;
    }

    const taskId = startGenerationTask(pending);
    activeVideoNotificationTaskIdRef.current = taskId;
    setShowVideoNotificationPrompt(false);

    const completed = completedVideoNotificationRef.current;
    if (completed) {
      completeGenerationTask(taskId, {
        ...pending,
        ...completed,
      });
      completedVideoNotificationRef.current = null;
      pendingVideoNotificationRef.current = null;
    }
  }, [completeGenerationTask, startGenerationTask]);

  const handleNewChat = useCallback(() => {
    resetLocalChat();
    setActiveGenerateConversationId(null);
    createNewChat(selectedClient);
    refreshGenerateConversations();
  }, [
    createNewChat,
    selectedClient,
    refreshGenerateConversations,
    resetLocalChat,
  ]);

  const handleSelectConversation = useCallback(
    async (session) => {
      if (!session) return;

      resetLocalChat();
      const selectedConversationId = getSessionId(session);
      const requestKey = String(selectedConversationId ?? "");
      setActiveGenerateConversationId(selectedConversationId);
      await loadChatSession(session);

      if (
        selectedConversationId &&
        !isTemporaryConversationId(selectedConversationId)
      ) {
        try {
          const apiMessages =
            await messageService.getMessagesByConversationId(
              selectedConversationId,
            );
          const mappedMessages = mapApiMessagesToGenerateMessages(apiMessages);
          setMessages(mappedMessages);
          const hydratedMessages = await hydrateGenerateMessagesMedia(
            selectedConversationId,
            mappedMessages,
          );

          hydratedMessages.forEach((msg) => {
            if (msg.blobUrlId && msg.content?.startsWith("blob:")) {
              blobUrlsRef.current.set(msg.blobUrlId, msg.content);
            }
            const attachmentBlobs = Array.isArray(msg.attachmentBlobUrls)
              ? msg.attachmentBlobUrls
              : [msg.attachmentPreviewUrl, msg.attachmentUrl];
            attachmentBlobs.forEach((url, index) => {
              if (String(url ?? "").startsWith("blob:")) {
                blobUrlsRef.current.set(
                  `${msg.blobUrlId || `attachment-${msg.messagesId}`}-${index}`,
                  url,
                );
              }
            });
          });

          setMessages((prev) => {
            if (String(selectedConversationId ?? "") !== requestKey) {
              return prev;
            }
            return hydratedMessages;
          });
        } catch (error) {
          logApiError("Load generate conversation messages", error);
          setMessages([]);
          showAlert?.(
            "Something failed while loading the data.",
            getUserErrorMessage(error, "Something failed while loading the data."),
          );
        }
      }
    },
    [loadChatSession, resetLocalChat, showAlert],
  );

  useEffect(() => {
    const targetConversationId = location.state?.openGenerationConversationId;
    if (!targetConversationId || isTemporaryConversationId(targetConversationId)) {
      handledNotificationConversationRef.current = null;
      return;
    }

    const targetKey = String(targetConversationId);
    if (handledNotificationConversationRef.current === targetKey) {
      return;
    }

    const targetSession =
      generateConversations.find(
        (session) => String(getSessionId(session) ?? "") === targetKey,
      ) ?? {
        conversationsId: targetConversationId,
        conversationId: targetConversationId,
        title: location.state?.openGenerationPrompt || "Generated video",
      };

    handledNotificationConversationRef.current = targetKey;
    handleSelectConversation(targetSession);
    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [
    generateConversations,
    handleSelectConversation,
    location.pathname,
    location.search,
    location.state?.openGenerationConversationId,
    location.state?.openGenerationPrompt,
    navigate,
  ]);

  const handleRenameConversation = useCallback(
    async (session, newTitle) => {
      const ok = await renameChatSession(session, newTitle);
      if (ok) await refreshGenerateConversations();
      return ok;
    },
    [renameChatSession, refreshGenerateConversations],
  );

  const handleDeleteConversation = useCallback(
    async (session) => {
      const conversationId = getSessionId(session);
      if (!conversationId || isTemporaryConversationId(conversationId)) {
        return;
      }

      const deleteKey = String(conversationId);
      setDeletingGenerateConversationIds((prev) => ({ ...prev, [deleteKey]: true }));
      try {
        await deleteConversationById(conversationId);
        if (String(activeGenerateConversationId ?? "") === deleteKey) {
          resetLocalChat();
          setActiveGenerateConversationId(null);
        }
        await refreshGenerateConversations();
      } catch (error) {
        logApiError("Delete generate conversation", error);
        showAlert?.(
          "Delete failed",
          getUserErrorMessage(
            error,
            "We couldn't delete this conversation. Please try again.",
          ),
        );
      } finally {
        setDeletingGenerateConversationIds((prev) => {
          const next = { ...prev };
          delete next[deleteKey];
          return next;
        });
      }
    },
    [
      activeGenerateConversationId,
      refreshGenerateConversations,
      resetLocalChat,
      showAlert,
    ],
  );

  const handleSend = useCallback(async (attachment = null) => {
    const fileToSend =
      typeof Blob !== "undefined" && attachment instanceof Blob
        ? attachment
        : null;
    const currentPrompt = prompt.trim();
    if ((!currentPrompt && !fileToSend) || !selectedClient || isGenerating) {
      return;
    }

    const userMessage = {
      type: "user",
      content: currentPrompt,
      ...(fileToSend?.name ? { attachmentName: fileToSend.name } : {}),
    };

    if (!getAuthToken()) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          type: "bot",
          content: "Authentication required. Please log in and try again.",
        },
      ]);
      return;
    }

    const clientId = getClientId(selectedClient);
    const silosId = generateSiloId ?? selectedSilo ?? null;

    if (!clientId) return;
    if (!silosId) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          type: "bot",
          content: "Generate silo is not available. Please try again later.",
        },
      ]);
      return;
    }

    if (!currentChatSession) {
      createNewChat(selectedClient);
    }

    const currentMediaType = isScriptOnlyPage ? "content" : mediaType;
    const currentRoute = location.pathname;
    const loadingId = Date.now();
    const isImageAttachment =
      fileToSend?.type?.startsWith("image/") ||
      /\.(png|jpe?g|gif|webp|jfif|bmp|svg)$/i.test(fileToSend?.name || "");
    if (isImageAttachment) {
      const previewUrl = URL.createObjectURL(fileToSend);
      userMessage.attachmentPreviewUrl = previewUrl;
      blobUrlsRef.current.set(`user-attachment-${loadingId}`, previewUrl);
    }
    const shouldNotifyGeneration = currentMediaType === "video";
    if (shouldNotifyGeneration) {
      pendingVideoNotificationRef.current = {
        prompt: currentPrompt,
        mediaType: currentMediaType,
        route: currentRoute,
      };
      activeVideoNotificationTaskIdRef.current = null;
      completedVideoNotificationRef.current = null;
      setShowVideoNotificationPrompt(true);
    } else {
      pendingVideoNotificationRef.current = null;
      setShowVideoNotificationPrompt(false);
    }
    let generatedMediaUrl = null;

    setMessages((prev) => [
      ...prev,
      userMessage,
      {
        id: loadingId,
        type: "bot",
        loading: true,
        mediaType: currentMediaType,
      },
    ]);

    setPrompt("");
    setIsGenerating(true);

    try {
      const streamResult = await generateStream({
        silosId: Number(silosId),
        conversationId: resolvedConversationId,
        modelId: selectedProject?.modelId ?? selectedProject?.ModelId ?? 1,
        projectId: getProjectId(selectedProject),
        clientId: Number(clientId),
        globallyAvailable: chatType !== "private",
        contentText: currentPrompt,
        mediaType: currentMediaType,
        aiName: getGenerateAiNameFromPath(location.pathname),
        ...(fileToSend ? { attachment: fileToSend } : {}),
      });

      if (streamResult?.responseType === "media" && streamResult.mediaUrl) {
        generatedMediaUrl = streamResult.mediaUrl;
        blobUrlsRef.current.set(loadingId, generatedMediaUrl);

        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === loadingId
              ? currentMediaType === "video"
                ? {
                    type: "bot",
                    content: generatedMediaUrl,
                    blobUrlId: loadingId,
                    isImage: false,
                    isVideo: true,
                    originalPrompt: currentPrompt,
                  }
                : {
                    type: "bot",
                    content: generatedMediaUrl,
                    blobUrlId: loadingId,
                    isImage: true,
                    isVideo: false,
                    originalPrompt: currentPrompt,
                  }
              : msg,
          ),
        );
      } else {
        const parsed = parseGenerateStreamResult(
          streamResult?.contentText ?? streamResult?.response ?? "",
          currentMediaType,
        );

        if (parsed.kind === "content") {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === loadingId
                ? { type: "bot", content: parsed.data, isContent: true }
                : msg,
            ),
          );
        } else {
          const mediaUrl = parsed.url;
          generatedMediaUrl = mediaUrl;
          if (mediaUrl.startsWith("blob:")) {
            blobUrlsRef.current.set(loadingId, mediaUrl);
          }

          setMessages((prev) =>
            prev.map((msg) =>
              msg.id === loadingId
                ? currentMediaType === "video"
                  ? {
                      type: "bot",
                      content: mediaUrl,
                      blobUrlId: mediaUrl.startsWith("blob:")
                        ? loadingId
                        : undefined,
                      isImage: false,
                      isVideo: true,
                      originalPrompt: currentPrompt,
                    }
                  : {
                      type: "bot",
                      content: mediaUrl,
                      blobUrlId: mediaUrl.startsWith("blob:")
                        ? loadingId
                        : undefined,
                      isImage: true,
                      isVideo: false,
                      originalPrompt: currentPrompt,
                    }
                : msg,
            ),
          );
        }
      }

      const conversationId = await persistConversationAfterSend(
        silosId,
        currentPrompt || fileToSend?.name || "New Chat",
      );
      if (shouldNotifyGeneration && generatedMediaUrl) {
        const result = {
          mediaType: currentMediaType,
          prompt: currentPrompt,
          mediaUrl: generatedMediaUrl,
          conversationId,
          route: currentRoute,
        };
        const notificationTaskId = activeVideoNotificationTaskIdRef.current;
        if (notificationTaskId) {
          completeGenerationTask(notificationTaskId, result);
          pendingVideoNotificationRef.current = null;
        } else {
          completedVideoNotificationRef.current = result;
        }
        setShowVideoNotificationPrompt(false);
      }
    } catch (error) {
      logApiError("Generate content", error);
      const errorText = getUserErrorMessage(
        error,
        "Generation could not be completed. Please try again.",
      );
      if (shouldNotifyGeneration) {
        const notificationTaskId = activeVideoNotificationTaskIdRef.current;
        if (notificationTaskId) {
          if (generatedMediaUrl) {
            completeGenerationTask(notificationTaskId, {
              mediaType: currentMediaType,
              prompt: currentPrompt,
              mediaUrl: generatedMediaUrl,
              route: currentRoute,
            });
          } else {
            failGenerationTask(notificationTaskId, {
              mediaType: currentMediaType,
              prompt: currentPrompt,
              error: errorText,
              route: currentRoute,
            });
          }
          pendingVideoNotificationRef.current = null;
        }
        setShowVideoNotificationPrompt(false);
      }
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingId ? { type: "bot", content: errorText } : msg,
        ),
      );
    } finally {
      setIsGenerating(false);
    }
  }, [
    prompt,
    mediaType,
    selectedClient,
    selectedProject,
    generateSiloId,
    selectedSilo,
    location.pathname,
    chatType,
    resolvedConversationId,
    currentChatSession,
    createNewChat,
    isGenerating,
    persistConversationAfterSend,
    showAlert,
    startGenerationTask,
    completeGenerationTask,
    failGenerationTask,
    isScriptOnlyPage,
  ]);

  const sortedGenerateConversations = useMemo(() => {
    if (!Array.isArray(generateConversations)) return [];
    return [...generateConversations].sort(
      (a, b) =>
        new Date(b.createdAt || b.date || 0) -
        new Date(a.createdAt || a.date || 0),
    );
  }, [generateConversations]);

  return {
    messages,
    prompt,
    setPrompt,
    mediaType,
    setMediaType,
    showVideoNotificationPrompt,
    dismissVideoNotificationPrompt,
    confirmVideoNotification,
    copiedMessageId,
    setCopiedMessageId,
    messagesEndRef,
    handleDownload,
    revealGeneratedVideo,
    handleSend,
    conversations: sortedGenerateConversations,
    currentChatSessionId:
      resolvedConversationId || getSessionId(currentChatSession),
    loadingConversations: loadingGenerateConversations,
    deletingConversationIds: deletingGenerateConversationIds,
    renamingConversationIds,
    isGenerating,
    showAlert,
    handleNewChat,
    handleSelectConversation,
    handleDeleteConversation,
    handleRenameConversation,
    isScriptOnlyPage,
  };
}
